import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastLogisticianRights } from "@/lib/rights/use-viewer-has-right";
import { useLineItemsListSections } from "@/pages/(app)/returns/refund/hooks/use-line-items-list-sections";
import { useCanLogisticianRevalidateAfterMerchant } from "@/pages/(app)/returns/hooks/use-can-logistician-revalidate-after-merchant";
import useLineItemsQuantitiesSummary from "@/pages/(app)/returns/refund/hooks/use-line-items-quantities-summary";
import {
  useNotTreatedByLogistician,
  useSetNotTreatedByLogistician,
} from "@/pages/(app)/returns/refund/hooks/use-line-items-state-context";
import { useRefundLineItem } from "@/pages/(app)/returns/refund/hooks/use-refund-line-items-context";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import NumberFlow from "@number-flow/react";
import { ArticleItem } from "../article-item/article-item";
import { getQuantityToHandleLogistician } from "./use-refund.view-model";

export default function ArticlesNotTreatedByLogistician() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { itemsNotTreatedByLogistician } = useLineItemsListSections();
  const hasPermissionToView = useViewerHasAtLeastLogisticianRights();
  const summary = useLineItemsQuantitiesSummary({
    state: "not-treated-by-logistician",
  });

  /**
   * If the shop has activated the logistician workflow, we don't show this section,
   * because it's useless
   */
  if (!shop.workflowLogistician.activated) {
    return null;
  }

  if (!itemsNotTreatedByLogistician.length || !hasPermissionToView) {
    return null;
  }

  return (
    <AccordionItem value="notTreatedByLogistician">
      <AccordionTrigger>
        <span>
          {t("returnDetail.confirmModal.notTreatedByLogistician")}
          <Badge variant="secondary" className="ml-2">
            <NumberFlow value={summary.selected} suffix={` / ${summary.max}`} />
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
          {itemsNotTreatedByLogistician.map((lineItem) => (
            <ArticleNotTreatedBylogistician
              key={lineItem.line_item_id}
              lineItem={lineItem}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

const ArticleNotTreatedBylogistician = ({
  lineItem,
}: {
  lineItem: ReturnRequestLineItemDto;
}) => {
  const { item, updateResolution, updateVariant } = useRefundLineItem(lineItem);
  const quantityValue = useNotTreatedByLogistician(lineItem.line_item_id);
  const updateQuantity = useSetNotTreatedByLogistician(lineItem.line_item_id);
  const canRevalidateAfterMerchant = useCanLogisticianRevalidateAfterMerchant();

  return (
    <ArticleItem
      lineItem={item}
      quantity={quantityValue.selectedQuantity}
      maxQuantity={getQuantityToHandleLogistician(lineItem, {
        allowRevalidationAfterMerchant: canRevalidateAfterMerchant,
      })}
      onChangeQuantity={updateQuantity}
      onChangeVariant={updateVariant}
      onChangeResolution={updateResolution}
      hideStatus
    />
  );
};
